<template>
  <div ref="navigationListContainer">
    <div v-if="showSubtitle" class="mb-4">
      <h2 v-if="title" class="h5 mb-3 text-dark fw-bold">{{ title }}</h2>

      <div class="d-flex flex-column gap-2 text-muted small">
        <div class="d-flex gap-2">
          <i
            class="bi bi-cursor-fill mt-1 flex-shrink-0 text-secondary"
            aria-hidden="true"
          ></i>
          <span
            >Select an island from the list below or click any marker on the map
            to view and populate fact sheets.</span
          >
        </div>

        <div class="d-flex gap-2">
          <i
            class="bi bi-layers-fill mt-1 flex-shrink-0 text-secondary"
            aria-hidden="true"
          ></i>
          <span
            >Use the <strong>Map Tools</strong> panel to toggle layers like
            Cultural Regions and Migration Routes.</span
          >
        </div>

        <div class="d-flex gap-2">
          <i
            class="bi bi-arrows-move mt-1 flex-shrink-0 text-secondary"
            aria-hidden="true"
          ></i>
          <span aria-hidden="true"
            >Zoom in to explore specific areas or use the Home button to reset
            your view.</span
          >
          <span class="visually-hidden"
            >Use tab keys to navigate the list, Enter to select, and Escape to
            return to the list.</span
          >
        </div>
      </div>
    </div>
    <h2 v-else-if="title" class="island-navigation-title">{{ title }}</h2>

    <hr v-if="showSubtitle" class="my-4 text-muted opacity-25" />

    <h3
      v-if="showSubtitle"
      class="h6 text-uppercase text-muted fw-bold mb-3"
      style="letter-spacing: 0.05em"
    >
      Islands & Regions
    </h3>

    <div class="island-list" role="list" aria-label="Islands">
      <button
        v-for="island in sortedIslands"
        :key="island[idField]"
        type="button"
        class="island-item"
        :aria-label="`Select ${island[nameField]}, island`"
        @click="handleSelect(island)"
        @keydown="handleKeyDown($event, island)"
      >
        <span class="island-name">
          <i
            v-if="island[idField] === assignedIslandId"
            class="bi bi-star-fill text-warning me-2"
            title="Your assigned island"
            aria-hidden="true"
          ></i>
          {{ island[nameField] }}
        </span>
        <i class="bi bi-chevron-right island-chevron" aria-hidden="true"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { IslandHeaders } from "@/types/index.ts";

export default {
  name: "IslandNavigationList",
  props: {
    /**
     * The title to display above the island list
     */
    title: {
      type: String,
      default: null
    },
    /**
     * Whether to show the subtitle/instructions
     */
    showSubtitle: {
      type: Boolean,
      default: true
    },
    /**
     * Array of island objects to display in the list
     */
    islands: {
      type: Array,
      required: true
    },
    /**
     * Field name to use for the island ID (for key binding)
     */
    idField: {
      type: String,
      default: IslandHeaders.ID
    },
    /**
     * Field name to use for display name
     */
    nameField: {
      type: String,
      default: IslandHeaders.NAME
    },
    /**
     * Sort order for the island list
     * Options: 'alphabetical', 'original'
     */
    sortBy: {
      type: String,
      default: "original",
      validator: (value) => ["alphabetical", "original"].includes(value)
    },
    /**
     * ID of the user's assigned island
     */
    assignedIslandId: {
      type: String,
      default: null
    }
  },
  emits: ["select"],
  computed: {
    /**
     * Returns islands sorted according to the sortBy prop
     * @returns {Array} Sorted array of islands
     */
    sortedIslands() {
      if (this.sortBy === "alphabetical") {
        return [...this.islands].sort((a, b) => {
          const nameA = (a[this.nameField] || "").toLowerCase();
          const nameB = (b[this.nameField] || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });
      }
      // 'original' - return as-is (CSV order)
      return this.islands;
    }
  },
  methods: {
    /**
     * Handles island selection and emits the select event
     * @param {Object} island - The selected island object
     */
    handleSelect(island) {
      this.$emit("select", island);
    },
    /**
     * Handles keyboard navigation for the list items
     * @param {KeyboardEvent} event - The keyboard event
     * @param {Object} island - The island being navigated
     */
    handleKeyDown(event, island) {
      // Find the parent list container
      const listContainer = event.target.closest(".island-list");
      if (!listContainer) return;

      // Get all buttons within this list
      const allButtons = Array.from(listContainer.querySelectorAll("button"));
      const currentIndex = allButtons.indexOf(event.target);

      if (currentIndex === -1) return;

      switch (event.key) {
        case "ArrowDown": {
          event.preventDefault();
          const nextIndex =
            currentIndex < allButtons.length - 1 ? currentIndex + 1 : 0;
          allButtons[nextIndex].focus();
          break;
        }
        case "ArrowUp": {
          event.preventDefault();
          const prevIndex =
            currentIndex > 0 ? currentIndex - 1 : allButtons.length - 1;
          allButtons[prevIndex].focus();
          break;
        }
        case "Home":
          event.preventDefault();
          allButtons[0].focus();
          break;
        case "End":
          event.preventDefault();
          allButtons[allButtons.length - 1].focus();
          break;
        case "Enter":
        case " ":
          event.preventDefault();
          this.handleSelect(island);
          break;
        default:
          // Allow other keys to work normally
          return;
      }
    },
    /**
     * Focuses the first button in the navigation list
     * Used for accessibility when returning to the list
     */
    focusFirstButton() {
      this.$nextTick(() => {
        const firstButton = this.$el.querySelector(".island-list button");
        if (firstButton) {
          firstButton.focus();
        }
      });
    }
  },
  mounted() {
    // No additional setup required here; native buttons are already focusable.
  }
};
</script>

<style scoped>
.island-navigation-title {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #2c3e50;
  letter-spacing: -0.01em;
}

.island-navigation-subtitle {
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 1.25rem;
  line-height: 1.5;
}

.island-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.island-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.875rem 1rem;
  background-color: var(--bs-white);
  border: 1px solid var(--bs-gray-200);
  border-radius: 0.5rem;
  text-align: left;
  font-size: 0.9375rem;
  color: #2c3e50;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.island-item::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 3px;
  background-color: var(--bs-primary);
  transform: scaleY(0);
  transform-origin: bottom;
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.island-item:hover {
  background-color: #f8f9fa;
  border-color: var(--bs-primary);
  color: #2c3e50;
  transform: translateX(2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.island-item:hover::before {
  transform: scaleY(1);
}

.island-item:hover .island-chevron {
  color: var(--bs-primary);
  transform: translateX(2px);
}

.island-item:active {
  transform: translateX(1px);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.island-item:focus {
  outline: none;
  border-color: var(--bs-primary);
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
}

.island-name {
  font-weight: 500;
  letter-spacing: -0.01em;
  flex: 1;
}

.island-chevron {
  font-size: 0.875rem;
  color: var(--bs-secondary);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  margin-left: 0.75rem;
}

/* Active/selected state (if needed in future) */
.island-item.active {
  background-color: rgba(13, 110, 253, 0.08);
  border-color: var(--bs-primary);
  color: var(--bs-primary);
}

.island-item.active::before {
  transform: scaleY(1);
}

.island-item.active .island-chevron {
  color: var(--bs-primary);
}
</style>
